import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MenuItem, MessageService, TreeNode } from 'primeng/api';
import { forkJoin, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { AccountingReportsService, ReportDiagnostic } from '../../accounting-reports.service';
import { GeneralLedger } from './general-ledger.model';
import { CurrencyPipe, CommonModule } from '@angular/common';
import * as XLSX from 'xlsx';
import { JournalEntriesService } from "../../journal-entries/journal-entries.service";
import { Journal } from "../../journal-entries/models/journal.model";
import { PartyService } from "../../../party/party.service";
import { Party } from "../../../party/party.model";
import { DimensionTypeService } from "../../journal-entries/dimension-type.service";
import { DimensionType } from "../../journal-entries/models/dimension-type.model";
import { DimensionValue } from "../../journal-entries/models/dimension-value.model";
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TreeSelectModule } from 'primeng/treeselect';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { PanelModule } from "primeng/panel";
import { DividerModule } from "primeng/divider";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { FloatLabelModule } from "primeng/floatlabel";
import { TagModule } from "primeng/tag";
import { ChipModule } from "primeng/chip";
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';

// Alias for the backend-owned running balance used by the table display.
interface DisplayGeneralLedger extends GeneralLedger {
    runningBalance?: number;
}

@Component({
    selector: 'app-general-ledger',
    templateUrl: './general-ledger.component.html',
    styleUrls: ['./general-ledger.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        TreeSelectModule,
        ButtonModule,
        InputTextModule,
        DatePickerModule,
        MultiSelectModule,
        SelectModule,
        TableModule,
        TooltipModule,
        ToastModule,
        ProgressBarModule,
        PanelModule,
        DividerModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        FloatLabelModule,
        TagModule,
        ChipModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        AccountingReportsService, ChartOfAccountsService,
        JournalEntriesService,
        PartyService,
        DimensionTypeService,
        CurrencyPipe
    ]

})
export class GeneralLedgerComponent implements OnInit, OnDestroy {

    companyId?: number;
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    selectedAccounts: TreeNode[] = [];
    chartOfAccountsTree: TreeNode<ChartOfAccounts>[] = [];

    // Entries displayed in the table after all filters are applied
    allFilteredLedgerEntries: DisplayGeneralLedger[] = [];

    isLoading: boolean = false;

    // Pagination properties
    totalRecords: number = 0;
    rows: number = 25;
    first: number = 0;

    // Filter properties
    filterDateRange: Date[] | null = null;
    filterTransactionNumber: string | null = null;
    filterReference: string | null = null;
    filterDescription: string | null = null;
    filterLogisticsProjectId: number | null = null;

    journals: Journal[] = [];

    partyTypes: string[] = [];
    parties: Party[] = [];

    // Models for the multi-select dropdowns
    filterJournals: Journal[] = [];
    filterPartyTypes: string[] = [];
    selectedParties: Party[] = [];
    partnerNameOptions: Party[] = [];

    // Dimension Filter properties
    filterDimensionTypes: number[] = [];
    selectedDimensionValues: number[] = [];
    dimensionTypes: DimensionType[] = [];
    dimensionValueOptions: DimensionValue[] = [];
    dynamicDimensionColumns: DimensionType[] = [];

    // Summary properties
    openingBalance: number = 0;
    openingDebitBalance: number = 0;
    openingCreditBalance: number = 0;
    totalFilteredDebits: number = 0;
    totalFilteredCredits: number = 0;
    closingBalance: number = 0;
    closingDebitBalance: number = 0;
    closingCreditBalance: number = 0;
    // reportCurrencyCode is for the summary totals. Individual rows will use ledger.currencyCode.
    reportCurrencyCode: string = 'USD';
    diagnostics: ReportDiagnostic[] = [];
    metrics: PageMetric[] = [];

    private subscriptions: Subscription = new Subscription();

    constructor(
        private accountingReportsService: AccountingReportsService,
        private sessionStorageService: SessionStorageService,
        private chartOfAccountsService: ChartOfAccountsService,
        private messageService: MessageService,
        private partyService: PartyService,
        private journalsService: JournalEntriesService,
        private dimensionTypeService: DimensionTypeService,
        private currencyPipe: CurrencyPipe
    ) {
        this.companyId = this.sessionStorageService.getCompany()?.id;
    }

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Accounts', routerLink: '/accounts' },
            { label: 'Enterprise', routerLink: '/accounts/enterprise' },
            { label: 'General Ledger' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.determineReportCurrency();
        this.updateMetrics();
        this.loadChartOfAccountsTree();
        this.loadJournals();
        this.loadParties();

        this.loadDimensionTypes();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private determineReportCurrency(): void {
        const company = this.sessionStorageService.getCompany();
        if (company?.countryId) {
            const country = this.sessionStorageService.getCountryList()?.find(c => c.id === company.countryId);
            if (country?.currencyCode) {
                this.reportCurrencyCode = country.currencyCode;
            }
        }
    }

    loadChartOfAccountsTree(): void {
        // this.isLoading is managed by the primary data loading function: loadLedgerEntries.

        if (!this.companyId) {
            this.handleError('Company ID not found for loading Chart of Accounts.', null, true);
            return;
        }

        this.subscriptions.add(
            this.chartOfAccountsService.getAllChartOfAccountsByCompany(this.companyId, true).subscribe({
                next: (accounts) => this.chartOfAccountsTree = accounts.map(account => this.buildTreeNode(account)),
                error: (error) => this.handleError('Error loading Chart of Accounts', error)
            })
        );
    }

    loadJournals(): void {
        this.journalsService.getAllJournalsByCompany(this.companyId ?? 0).subscribe({ // Assuming a getJournals() method
            next: (data) => {
                this.journals = data; // Or data.content if it's paginated
            },
            error: (err) => {
                console.error('Failed to load journals', err);
                // Optionally show a toast message
            }
        });
    }

    loadParties(): void {
        this.partyService.getParties(this.companyId ?? 0, [], "", 0, 1000) // Fetch all for dropdown
            .subscribe({
                next: (response) => {
                    this.parties = response.content;
                    this.partyTypes = [...new Set(response.content.map(party => party.id.entityType))];
                },
                error: (err) => {
                    console.error('Failed to load partners');
                    // Optionally show a user-facing error message
                }
            });
    }

    loadDimensionTypes(): void {
        if (!this.companyId) return;
        this.subscriptions.add(
            this.dimensionTypeService.getDimensionTypesByCompany(this.companyId).subscribe({
                next: (types) => {
                    this.dimensionTypes = types;
                },
                error: (err) => this.handleError('Failed to load dimension types', err, true)
            })
        );
    }

    buildTreeNode(chartOfAccounts: ChartOfAccounts): TreeNode<ChartOfAccounts> {
        return {
            label: chartOfAccounts.accountName, // Display only account name
            data: chartOfAccounts,
            key: chartOfAccounts.accountId?.toString(),
            expandedIcon: 'pi pi-folder-open',
            collapsedIcon: 'pi pi-folder',
            leaf: (chartOfAccounts.childrenAccounts?.length ?? 0) === 0,
            expanded: false,
            children: chartOfAccounts.childrenAccounts?.map(child => this.buildTreeNode(child))
        };
    }

    private getSelectedAccountIds(nodes: TreeNode<ChartOfAccounts>[]): number[] {
        const accountIds: Set<number> = new Set();
        const collectIds = (node: TreeNode<ChartOfAccounts>) => {
            if (node.data?.accountId) {
                accountIds.add(node.data.accountId);
            }
            if (node.children) {
                node.children.forEach(collectIds);
            }
        };
        nodes.forEach(collectIds);
        return Array.from(accountIds);
    }

    private getCurrentFilters(): any {
        const selectedAccountIds = this.getSelectedAccountIds(this.selectedAccounts);
        const startDate = this.filterDateRange?.[0] ? this.filterDateRange[0].toISOString().split('T')[0] : undefined;
        const endDate = this.filterDateRange?.[1] ? this.filterDateRange[1].toISOString().split('T')[0] : undefined;
        const journals = this.filterJournals.map(j => j.journalName);
        const partyIds = this.selectedParties.map(p => p.id.entityId);

        return {
            accountIds: selectedAccountIds,
            startDate: startDate,
            endDate: endDate,
            transactionNumber: this.filterTransactionNumber || undefined,
            reference: this.filterReference || undefined,
            description: this.filterDescription || undefined,
            logisticsProjectId: this.filterLogisticsProjectId || undefined,
            journals: journals,
            partyTypes: this.filterPartyTypes,
            partyIds: partyIds,
            dimensionTypeIds: this.filterDimensionTypes,
            dimensionValueIds: this.selectedDimensionValues
        };
    }

    loadLedgerEntries(event?: TableLazyLoadEvent): void {
        this.isLoading = true;
        if (event) {
            this.first = event.first ?? 0;
            this.rows = event.rows ?? 25;
        }

        if (!this.companyId) {
            this.handleError('Company ID not found.', null, true);
            return;
        }

        const filters = this.getCurrentFilters();
        const page = this.first / this.rows;
        const size = this.rows;

        this.subscriptions.add(
            this.accountingReportsService.getGeneralLedgerV2(this.companyId, page, size, filters)
                .pipe(finalize(() => (this.isLoading = false)))
                .subscribe({
                    next: (ledgerPage) => {
                        const summary = ledgerPage.summary;
                        // Assign summary values from backend
                        this.openingBalance = Number(summary.openingBalance ?? 0);
                        this.openingDebitBalance = Number(summary.openingDebitBalance ?? 0);
                        this.openingCreditBalance = Number(summary.openingCreditBalance ?? 0);
                        this.totalFilteredDebits = Number(summary.totalDebits ?? 0);
                        this.totalFilteredCredits = Number(summary.totalCredits ?? 0);
                        this.closingBalance = Number(summary.closingBalance ?? 0);
                        this.closingDebitBalance = Number(summary.closingDebitBalance ?? 0);
                        this.closingCreditBalance = Number(summary.closingCreditBalance ?? 0);
                        this.reportCurrencyCode = summary.currencyCode || this.reportCurrencyCode;
                        this.diagnostics = ledgerPage.diagnostics || [];

                        // Assign paginated data
                        this.allFilteredLedgerEntries = ledgerPage.content.map(entry => ({
                            ...entry,
                            transactionDate: entry.transactionDate ? new Date(entry.transactionDate) : null,
                            debit: typeof entry.debit === 'string' ? parseFloat(entry.debit) : entry.debit,
                            credit: typeof entry.credit === 'string' ? parseFloat(entry.credit) : entry.credit,
                            balance: typeof entry.balance === 'string' ? parseFloat(entry.balance) : entry.balance,
                            runningBalance: typeof entry.balance === 'string' ? parseFloat(entry.balance) : Number(entry.balance ?? 0),
                        }));
                        this.totalRecords = ledgerPage.totalElements;
                        this.updateMetrics();
                    },
                    error: (error) => this.handleError('Error loading General Ledger data', error)
                })
        );
    }

    applyFilters(): void {
        this.loadLedgerEntries({ first: 0, rows: this.rows });
    }

    // Add this new method inside your component class
    onPartnerTypeChange(): void {
        // When types change, clear the subsequent selections and options
        this.selectedParties = [];
        this.partnerNameOptions = [];

        // If any types are selected, fetch all corresponding parties
        if (this.filterPartyTypes && this.filterPartyTypes.length > 0) {
            const companyId = this.sessionStorageService.getCompany()?.id;
            if (!companyId) return;

            // Assuming your service can take an array of types
            this.partyService.getParties(companyId, this.filterPartyTypes, '', 0, 1000)
                .subscribe({
                    next: (response) => {
                        this.partnerNameOptions = response.content;
                    },
                    error: (err) => {
                        console.error('Failed to load parties for selected types:', err);
                    }
                });
        }
    }

    onDimensionTypeFilterChange(): void {
        // Reset dependent filters and dynamic columns
        this.selectedDimensionValues = [];
        this.dimensionValueOptions = [];
        this.dynamicDimensionColumns = this.filterDimensionTypes
            .map(typeId => this.dimensionTypes.find(dt => dt.dimensionTypeId === typeId))
            .filter((dt): dt is DimensionType => !!dt); // Type guard to filter out undefined

        if (this.filterDimensionTypes.length === 0) {
            return; // Exit if no types are selected
        }

        // Fetch values for all selected dimension types.
        const valueFetchObservables = this.dynamicDimensionColumns.map(dimType =>
            this.dimensionTypeService.getValuesForDimension(this.companyId ?? 0, dimType.tableName)
        );

        if (valueFetchObservables.length > 0) {
            this.subscriptions.add(
                forkJoin(valueFetchObservables).subscribe({
                    next: (results) => {
                        // Flatten the array of arrays into a single array for the dropdown options
                        this.dimensionValueOptions = results.flat();
                    },
                    error: (err) => this.handleError('Failed to load dimension values', err, true)
                })
            );
        }
    }

    getDimensionValue(ledgerEntry: GeneralLedger, dimensionTypeId: number): string {
        if (!ledgerEntry.dimensions) {
            return '';
        }
        const dimension = ledgerEntry.dimensions.find((d: any) => d.dimensionTypeId === dimensionTypeId);
        return dimension ? dimension.dimensionValueName : '';
    }

    rowCurrency(ledger: GeneralLedger): string {
        return this.safeCurrencyCode(ledger.currencyCode || this.reportCurrencyCode);
    }

    sourceLabel(ledger: GeneralLedger): string {
        const type = ledger.sourceDocumentType || 'Journal Entry';
        const number = ledger.sourceDocumentNumber || ledger.transactionNumber || ledger.sourceDocumentId;
        return number ? `${type}: ${number}` : type;
    }

    dimensionLabel(dimension: any): string {
        const typeName = dimension.dimensionTypeName || 'Dimension';
        const valueName = dimension.dimensionValueName || '-';
        return `${typeName}: ${valueName}`;
    }

    diagnosticSeverity(severity: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch ((severity || '').toUpperCase()) {
            case 'ERROR':
                return 'danger';
            case 'WARN':
            case 'WARNING':
                return 'warn';
            case 'INFO':
                return 'info';
            default:
                return 'secondary';
        }
    }

    private updateMetrics(): void {
        this.metrics = [
            {
                label: 'Opening Balances',
                value: this.formatBalanceSides(this.openingBalance, this.openingDebitBalance, this.openingCreditBalance),
                hint: `Net ${this.formatMoney(this.openingBalance, this.reportCurrencyCode)}`,
                tone: 'info'
            },
            {
                label: 'Debits',
                value: this.formatMoney(this.totalFilteredDebits, this.reportCurrencyCode),
                hint: 'Filtered movement',
                tone: 'danger'
            },
            {
                label: 'Credits',
                value: this.formatMoney(this.totalFilteredCredits, this.reportCurrencyCode),
                hint: 'Filtered movement',
                tone: 'success'
            },
            {
                label: 'Closing Balances',
                value: this.formatBalanceSides(this.closingBalance, this.closingDebitBalance, this.closingCreditBalance),
                hint: `Net ${this.formatMoney(this.closingBalance, this.reportCurrencyCode)} | ${this.totalRecords || 0} entries`,
                tone: 'info'
            }
        ];
    }

    formatBalanceSides(netBalance: number | string | null | undefined, debitBalance?: number, creditBalance?: number): string {
        const debit = Number(debitBalance ?? 0);
        const credit = Number(creditBalance ?? 0);
        if (debit > 0 || credit > 0) {
            const parts: string[] = [];
            if (debit > 0) {
                parts.push(`Dr ${this.formatMoney(debit, this.reportCurrencyCode)}`);
            }
            if (credit > 0) {
                parts.push(`Cr ${this.formatMoney(credit, this.reportCurrencyCode)}`);
            }
            return parts.join(' / ');
        }
        return this.formatMoney(netBalance, this.reportCurrencyCode);
    }

    formatMoney(value: number | string | null | undefined, currencyCode: string = this.reportCurrencyCode): string {
        const numericValue = Number(value) || 0;
        if (!this.isIsoCurrencyCode(currencyCode)) {
            return `${numericValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyCode || ''}`.trim();
        }
        return this.currencyPipe.transform(numericValue, currencyCode, 'symbol', '1.2-2') || '';
    }

    private formatDate(value: Date | string | null | undefined): string {
        return value ? new Date(value).toLocaleDateString('en-CA') : '';
    }

    private formatDimensionsForExport(entry: GeneralLedger): string {
        return (entry.dimensions || []).map(dimension => this.dimensionLabel(dimension)).join('; ');
    }

    private escapeHtml(value: unknown): string {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    private safeCurrencyCode(currencyCode: string | null | undefined): string {
        return this.isIsoCurrencyCode(currencyCode) ? currencyCode! : 'SAR';
    }

    private isIsoCurrencyCode(currencyCode: string | null | undefined): boolean {
        return /^[A-Z]{3}$/.test(currencyCode || '');
    }

    clearFilters(): void {
        this.filterDateRange = null;
        this.filterTransactionNumber = null;
        this.filterReference = null;
        this.filterDescription = null;
        this.filterLogisticsProjectId = null;

        this.filterJournals = [];
        this.filterPartyTypes = [];
        this.selectedParties = [];
        this.partnerNameOptions = []; // Clear the options for the dependent dropdown

        this.filterDimensionTypes = [];
        this.selectedDimensionValues = [];
        this.dimensionValueOptions = [];
        this.dynamicDimensionColumns = [];

        this.applyFilters(); // Re-apply to show all data with reset filters
    }

    private handleError(summary: string, error: any, showUserMessage: boolean = true): void {
        if (showUserMessage) {
            this.messageService.add({
                severity: 'error',
                summary: summary,
                detail: error?.message || 'An unknown error occurred. Please try again.'
            });
        }
        console.error(summary, error);
    }

    exportExcel(): void {
        if (this.totalRecords === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no data to export.' });
            return;
        }

        this.isLoading = true;
        const filters = this.getCurrentFilters();

        // Assuming a service method to get all filtered data without pagination
        this.subscriptions.add(
            this.accountingReportsService.getGeneralLedgerAllV2(this.companyId!, filters)
                .pipe(finalize(() => (this.isLoading = false)))
                .subscribe({
                    next: (response) => this.generateExcelFile(response.content),
                    error: (err) => this.handleError('Failed to export data', err, true)
                })
        );
    }

    private generateExcelFile(entries: GeneralLedger[]): void {
        const dataToExport: any[][] = [];

        dataToExport.push(['General Ledger Summary']);
        dataToExport.push(['Opening Debit Balance', this.openingDebitBalance]);
        dataToExport.push(['Opening Credit Balance', this.openingCreditBalance]);
        dataToExport.push(['Opening Net Balance', this.openingBalance]);
        dataToExport.push(['Total Debits', this.totalFilteredDebits]);
        dataToExport.push(['Total Credits', this.totalFilteredCredits]);
        dataToExport.push(['Closing Debit Balance', this.closingDebitBalance]);
        dataToExport.push(['Closing Credit Balance', this.closingCreditBalance]);
        dataToExport.push(['Closing Net Balance', this.closingBalance]);
        dataToExport.push(['Currency', this.reportCurrencyCode]);
        dataToExport.push([]);

        const tableHeader = [
            'Date', 'Account', 'Document', 'Party', 'Debit', 'Credit', 'Balance', 'Currency',
            'Description', 'Reference', 'Source', 'Dimensions', 'Journal Entry ID', 'Journal Line ID'
        ];
        dataToExport.push(tableHeader);

        entries.forEach(item => {
            const currencyCode = item.currencyCode || this.reportCurrencyCode;
            const row = [
                this.formatDate(item.transactionDate),
                `${item.accountCode || ''} ${item.accountName || ''}`.trim(),
                `${item.transactionNumber || item.sourceDocumentNumber || ''} ${item.journal ? `(${item.journal})` : ''}`.trim(),
                `${item.partnerName || ''} ${item.partnerType ? `(${item.partnerType})` : ''}`.trim(),
                Number(item.debit ?? 0),
                Number(item.credit ?? 0),
                Number(item.balance ?? 0),
                currencyCode,
                item.description || '',
                item.reference || '',
                this.sourceLabel(item),
                this.formatDimensionsForExport(item),
                item.jeEntryId || '',
                item.journalItemId || ''
            ];
            dataToExport.push(row);
        });

        const footerRow = [
            '', '', '', 'Totals for filtered period',
            this.totalFilteredDebits,
            this.totalFilteredCredits,
            this.closingBalance,
            this.reportCurrencyCode,
            '', '', '', '', '', ''
        ];
        dataToExport.push(footerRow);

        const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(dataToExport);
        const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, "GeneralLedgerReport");
        this.messageService.add({ severity: 'success', summary: 'Exported', detail: 'Report exported to Excel.' });
    }

    printReport(): void {
        if (this.totalRecords === 0 && !(this.filterDateRange && this.filterDateRange[0])) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no data to print.' });
            return;
        }

        this.isLoading = true;
        const filters = this.getCurrentFilters();
        this.subscriptions.add(
            this.accountingReportsService.getGeneralLedgerAllV2(this.companyId!, filters)
                .pipe(finalize(() => (this.isLoading = false)))
                .subscribe({
                    next: (response) => this.generatePrintableHtml(response.content),
                    error: (err) => this.handleError('Failed to load data for printing', err, true)
                })
        );
    }

    private generatePrintableHtml(entries: GeneralLedger[]): void {
        const company = this.sessionStorageService.getCompany();
        const user = this.sessionStorageService.getCustomer();
        const generationDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const fileName = 'GeneralLedgerReport_' + new Date().toISOString().slice(0, 10);

        const styles = `
            <style>
                @media print { body { -webkit-print-color-adjust: exact; } }
                body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #172033; font-size: 10px; }
                .report-header { display: flex; justify-content: space-between; gap: 20px; align-items: flex-start; border-bottom: 2px solid #334155; padding-bottom: 12px; margin-bottom: 18px; }
                .report-header .logo { max-height: 48px; margin-bottom: 6px; }
                h1, h2 { margin: 0; }
                .muted { color: #64748b; }
                .summary-table, .data-table { width: 100%; border-collapse: collapse; margin-bottom: 22px; }
                .summary-table td { padding: 8px; border: 1px solid #d7dee8; text-align: center; }
                .summary-table strong { display: block; font-size: 13px; margin-top: 3px; }
                .data-table th, .data-table td { border: 1px solid #d7dee8; padding: 6px; text-align: left; vertical-align: top; }
                .data-table th { background-color: #f1f5f9; font-weight: 700; }
                .total-row { background-color: #f8fafc; font-weight: 700; }
                .text-right { text-align: right !important; }
                small { color: #64748b; display: block; margin-top: 2px; }
            </style>
        `;

        const rows = entries.map(item => {
            const currencyCode = item.currencyCode || this.reportCurrencyCode;
            return `
                <tr>
                    <td>${this.escapeHtml(this.formatDate(item.transactionDate))}</td>
                    <td>
                        <strong>${this.escapeHtml(item.accountCode || '-')}</strong>
                        <small>${this.escapeHtml(item.accountName || '')}</small>
                    </td>
                    <td>
                        <strong>${this.escapeHtml(item.transactionNumber || item.sourceDocumentNumber || '-')}</strong>
                        <small>${this.escapeHtml(item.journal || '')}</small>
                        ${item.reference ? `<small>Ref: ${this.escapeHtml(item.reference)}</small>` : ''}
                    </td>
                    <td>
                        <strong>${this.escapeHtml(item.partnerName || '-')}</strong>
                        <small>${this.escapeHtml(item.partnerType || '')}</small>
                    </td>
                    <td class="text-right">${this.escapeHtml(this.formatMoney(item.debit, currencyCode))}</td>
                    <td class="text-right">${this.escapeHtml(this.formatMoney(item.credit, currencyCode))}</td>
                    <td class="text-right">${this.escapeHtml(this.formatMoney(item.balance, currencyCode))}</td>
                </tr>
                ${item.description || item.dimensions?.length ? `
                    <tr>
                        <td></td>
                        <td colspan="6">
                            <small>${this.escapeHtml(item.description || '')}</small>
                            <small>${this.escapeHtml(this.formatDimensionsForExport(item))}</small>
                            <small>${this.escapeHtml(this.sourceLabel(item))} | JE ${this.escapeHtml(item.jeEntryId || '-')} | Line ${this.escapeHtml(item.journalItemId || '-')}</small>
                        </td>
                    </tr>
                ` : ''}
            `;
        }).join('');

        const reportHtml = `
            <html>
                <head>
                    <title>${this.escapeHtml(fileName)}</title>
                    ${styles}
                </head>
                <body>
                    <div class="report-header">
                        <div>
                            ${company?.logo ? `<img src="${this.escapeHtml(company.logo)}" alt="Company Logo" class="logo">` : ''}
                            <h1>${this.escapeHtml(company?.name || 'Your Company')}</h1>
                            <div class="muted">General Ledger</div>
                        </div>
                        <div style="text-align: right;">
                            <strong>Printed By:</strong> ${this.escapeHtml(user?.name || 'N/A')}<br>
                            <strong>Date:</strong> ${this.escapeHtml(generationDate)}<br>
                            <strong>Currency:</strong> ${this.escapeHtml(this.reportCurrencyCode)}
                        </div>
                    </div>

                    <table class="summary-table">
                        <tr>
                            <td>Opening Balances<strong>${this.escapeHtml(this.formatBalanceSides(this.openingBalance, this.openingDebitBalance, this.openingCreditBalance))}</strong><small>Net ${this.escapeHtml(this.formatMoney(this.openingBalance))}</small></td>
                            <td>Total Debits<strong>${this.escapeHtml(this.formatMoney(this.totalFilteredDebits))}</strong></td>
                            <td>Total Credits<strong>${this.escapeHtml(this.formatMoney(this.totalFilteredCredits))}</strong></td>
                            <td>Closing Balances<strong>${this.escapeHtml(this.formatBalanceSides(this.closingBalance, this.closingDebitBalance, this.closingCreditBalance))}</strong><small>Net ${this.escapeHtml(this.formatMoney(this.closingBalance))}</small></td>
                        </tr>
                    </table>

                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Account</th>
                                <th>Document</th>
                                <th>Party</th>
                                <th class="text-right">Debit</th>
                                <th class="text-right">Credit</th>
                                <th class="text-right">Balance</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                        <tfoot>
                            <tr class="total-row">
                                <td colspan="4" class="text-right">Totals for filtered period</td>
                                <td class="text-right">${this.escapeHtml(this.formatMoney(this.totalFilteredDebits))}</td>
                                <td class="text-right">${this.escapeHtml(this.formatMoney(this.totalFilteredCredits))}</td>
                                <td class="text-right">${this.escapeHtml(this.formatBalanceSides(this.closingBalance, this.closingDebitBalance, this.closingCreditBalance))}</td>
                            </tr>
                        </tfoot>
                    </table>
                </body>
            </html>
        `;

        const printWindow = window.open('', '_blank', 'height=800,width=1200');
        if (printWindow) {
            printWindow.document.write(reportHtml);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        }
        this.messageService.add({ severity: 'info', summary: 'Print', detail: 'Print dialog initiated.' });
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const downloadLink: HTMLAnchorElement = document.createElement('a');
        const url = URL.createObjectURL(data);
        downloadLink.href = url;
        downloadLink.download = fileName + '_' + new Date().toISOString().slice(0, 10) + '.xlsx';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    }
}
